import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Features = () => {
  const { language } = useLanguage();

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  const content = {
    'en': {
      title: 'Beat Master Features',
      subtitle: 'Discover the powerful tools that make Beat Master the perfect rhythm companion',
      features: [
        {
          id: 'precision-timing',
          title: 'Precision Timing',
          icon: FiIcons.FiClock,
          description: 'Beat Master uses high-precision timing algorithms to ensure accurate and consistent beats, essential for serious music practice and performance.',
          details: [
            'Millisecond-accurate timing engine',
            'Stable tempo even during long practice sessions',
            'Audio-visual synchronization for enhanced learning',
            'Adjustable from 30 to 240 BPM'
          ]
        },
        {
          id: 'customizable-beats',
          title: 'Customizable Beats',
          icon: FiIcons.FiSliders,
          description: 'Personalize your practice sessions with fully customizable beat patterns, accents, and subdivisions to match any musical style or learning need.',
          details: [
            'Adjustable time signatures (2/4, 3/4, 4/4, 5/4, 6/8, 7/8, etc.)',
            'Beat accent customization for emphasizing specific beats',
            'Multiple subdivision options (quarter, eighth, triplet, sixteenth notes)',
            'Visual beat indicators with color differentiation'
          ]
        },
        {
          id: 'sound-options',
          title: 'Rich Sound Library',
          icon: FiIcons.FiMusic,
          description: 'Choose from a variety of metronome sounds to find the perfect tone for your practice environment and personal preference.',
          details: [
            'Multiple sound options (woodblock, click, drum, bell)',
            'Independent volume control for main beats and subdivisions',
            'Accent beat sound differentiation',
            'Master volume control for quick adjustments'
          ]
        },
        {
          id: 'intuitive-interface',
          title: 'Intuitive Interface',
          icon: FiIcons.FiLayout,
          description: 'Beat Master features a clean, responsive design that works beautifully across all devices, making rhythm practice accessible anywhere.',
          details: [
            'Simple, distraction-free layout',
            'Dark and light mode support',
            'Responsive design for desktop and mobile devices',
            'Accessibility features for all users'
          ]
        },
        {
          id: 'tempo-training',
          title: 'Tempo Training Tools',
          icon: FiIcons.FiTrendingUp,
          description: 'Improve your internal sense of timing with specialized tools designed to build rhythm independence and stability.',
          details: [
            'Tap tempo feature for finding your natural rhythm',
            'Visual metronome with animated beat indicators',
            'Tempo presets for common practice speeds',
            'Classical tempo markings (Adagio, Allegro, etc.)'
          ]
        },
        {
          id: 'multilingual-support',
          title: 'Multilingual Support',
          icon: FiIcons.FiGlobe,
          description: 'Practice in your preferred language with full support for English and Traditional Chinese interfaces.',
          details: [
            'Complete English and Traditional Chinese (繁體中文) translations',
            'Language-specific help documentation',
            'Automatic language detection based on browser settings',
            'Easy language switching with a single click'
          ]
        }
      ],
      ctaText: 'Start using Beat Master today!'
    },
    'zh-TW': {
      title: 'Beat Master 功能介紹',
      subtitle: '探索讓 Beat Master 成為完美節奏夥伴的強大工具',
      features: [
        {
          id: 'precision-timing',
          title: '精準計時',
          icon: FiIcons.FiClock,
          description: 'Beat Master 使用高精度計時演算法，確保準確且一致的節拍，這對於嚴肅的音樂練習和表演至關重要。',
          details: [
            '毫秒級精確的計時引擎',
            '即使在長時間練習過程中也能保持穩定的節奏',
            '音頻-視覺同步以增強學習效果',
            '可調整範圍從 30 到 240 BPM'
          ]
        },
        {
          id: 'customizable-beats',
          title: '自定義節拍',
          icon: FiIcons.FiSliders,
          description: '透過完全可自定義的節拍模式、重音和細分，個性化您的練習課程，以匹配任何音樂風格或學習需求。',
          details: [
            '可調整的拍號（2/4、3/4、4/4、5/4、6/8、7/8等）',
            '節拍重音自定義，用於強調特定拍子',
            '多種細分選項（四分音符、八分音符、三連音、十六分音符）',
            '帶有顏色區分的視覺節拍指示器'
          ]
        },
        {
          id: 'sound-options',
          title: '豐富的聲音庫',
          icon: FiIcons.FiMusic,
          description: '從多種節拍器聲音中選擇，找到適合您的練習環境和個人偏好的完美音調。',
          details: [
            '多種聲音選項（木魚、點擊聲、鼓聲、鈴聲）',
            '主節拍和細分的獨立音量控制',
            '重音拍聲音區分',
            '主音量控制，便於快速調整'
          ]
        },
        {
          id: 'intuitive-interface',
          title: '直覺式介面',
          icon: FiIcons.FiLayout,
          description: 'Beat Master 具有乾淨、響應式的設計，可在所有設備上完美運作，讓您隨時隨地練習節奏。',
          details: [
            '簡單、無干擾的布局',
            '支持深色和淺色模式',
            '適用於桌面和移動設備的響應式設計',
            '適合所有用戶的無障礙功能'
          ]
        },
        {
          id: 'tempo-training',
          title: '速度訓練工具',
          icon: FiIcons.FiTrendingUp,
          description: '透過專為建立節奏獨立性和穩定性而設計的專業工具，提高您的內在時間感。',
          details: [
            '點擊速度功能，用於找到您的自然節奏',
            '帶有動畫節拍指示器的視覺節拍器',
            '常見練習速度的預設',
            '古典速度標記（如慢板、快板等）'
          ]
        },
        {
          id: 'multilingual-support',
          title: '多語言支持',
          icon: FiIcons.FiGlobe,
          description: '使用您偏好的語言練習，完全支持英文和繁體中文介面。',
          details: [
            '完整的英文和繁體中文翻譯',
            '特定語言的幫助文檔',
            '基於瀏覽器設置的自動語言檢測',
            '一鍵輕鬆切換語言'
          ]
        }
      ],
      ctaText: '立即開始使用 Beat Master！'
    }
  };

  const currentContent = content[language] || content['en'];

  // Animation variants
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const featureVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.6, 
        ease: "easeOut" 
      }
    }
  };

  // Function to get the appropriate color class based on index
  const getColorClasses = (index) => {
    if (index % 3 === 0) {
      return {
        bg: 'bg-blue-100 dark:bg-blue-900/30',
        text: 'text-blue-600 dark:text-blue-400',
        gradient: 'from-blue-500 to-blue-300',
        darkGradient: 'dark:from-blue-400 dark:to-blue-600'
      };
    } else if (index % 3 === 1) {
      return {
        bg: 'bg-purple-100 dark:bg-purple-900/30',
        text: 'text-purple-600 dark:text-purple-400',
        gradient: 'from-purple-500 to-purple-300',
        darkGradient: 'dark:from-purple-400 dark:to-purple-600'
      };
    } else {
      return {
        bg: 'bg-pink-100 dark:bg-pink-900/30',
        text: 'text-pink-600 dark:text-pink-400',
        gradient: 'from-pink-500 to-pink-300',
        darkGradient: 'dark:from-pink-400 dark:to-pink-600'
      };
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4">
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
          {currentContent.title}
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300">
          {currentContent.subtitle}
        </p>
      </motion.div>

      <motion.div 
        className="space-y-16"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {currentContent.features.map((feature, index) => {
          const colorClasses = getColorClasses(index);
          
          return (
            <motion.section 
              key={feature.id} 
              id={feature.id}
              className="scroll-mt-20"
              variants={featureVariants}
            >
              <div className="flex flex-col md:flex-row gap-8 items-start">
                <div className="md:w-1/3 flex flex-col items-center md:items-start text-center md:text-left">
                  <div className={`p-4 rounded-full ${colorClasses.bg} ${colorClasses.text} mb-4`}>
                    <SafeIcon icon={feature.icon} className="h-8 w-8" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-3">
                    {feature.title}
                  </h2>
                  <p className="text-gray-600 dark:text-gray-300">
                    {feature.description}
                  </p>
                </div>
                
                <div className="md:w-2/3 bg-gray-50 dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                  <ul className="space-y-3">
                    {feature.details.map((detail, detailIndex) => (
                      <li key={detailIndex} className="flex items-start">
                        <div className={`p-1.5 rounded-full mr-3 mt-0.5 flex-shrink-0 ${colorClasses.bg}`}>
                          <SafeIcon icon={FiIcons.FiCheck} className={`h-3 w-3 ${colorClasses.text}`} />
                        </div>
                        <span className="text-gray-700 dark:text-gray-300">{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.section>
          );
        })}
      </motion.div>

      <motion.div 
        className="mt-16 text-center"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2, duration: 0.8 }}
      >
        {/* Add link to homepage */}
        <Link to="/">
          <div className="inline-block p-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg shadow-md text-white font-medium px-6 py-3 hover:opacity-90 transition-opacity">
            <div className="flex items-center gap-2">
              <SafeIcon icon={FiIcons.FiArrowRight} className="w-5 h-5" />
              <span>{currentContent.ctaText}</span>
            </div>
          </div>
        </Link>
      </motion.div>
    </div>
  );
};

export default Features;